import { createElement, type ReactNode } from 'react';

/**
 * Frequently-asked-questions for the home page.
 *
 * Single source of truth consumed by:
 *   - the <details> list in the home tab (App.tsx)
 *   - the mirror `FAQPage` JSON-LD block in `index.html`
 *
 * Keep the two in sync. Answers are written for both humans and search
 * crawlers — a ~1–3 sentence direct answer per question works best.
 *
 * `answer` is always a plain string so the JSON-LD FAQPage schema has
 * clean text to ingest. `answerRich` is an optional React node used only
 * in the visible UI — handy when an answer benefits from a real
 * `<a href>` (e.g. cross-linking a sister guide) while the schema still
 * carries the URL as plain text.
 */
export interface FaqItem {
  question: string;
  answer: string;
  answerRich?: ReactNode;
}

export const FAQ: FaqItem[] = [
  {
    question: 'How many cheesemakers does The Curd Compass cover?',
    answer:
      'The guide currently covers eighteen verified artisan cheeseries and twelve carefully chosen inns, B&Bs, and farm stays within practical driving distance of each. Every listing is cross-checked against state cheese guilds (the Vermont Cheese Council and Maine Cheese Guild today), the producer’s own website, and periodic calls to confirm hours.',
  },
  {
    question: 'Which cheeseries on the trail are actually open to the public?',
    answer:
      'Every listing carries a visitor policy. The five categories are: open-to-public (drop in during posted hours), by-appointment (call or email first), seasonal (summer and early fall only), production-only (no on-site visits, cheese sold at nearby retailers), and retail-partner (for example Jasper Hill Farm sells exclusively through Willey\u2019s Store in Greensboro Village). Use the filter chips on the Artisans tab to see only the drop-in-ready stops.',
  },
  {
    question: 'What’s the best time of year to drive the cheese trail?',
    answer:
      'Late May through mid-October covers the widest variety of open farm stores. Summer is ideal for Shelburne Farms and the coastal Maine properties. Peak foliage in late September and early October lines up well with Vermont\u2019s inland dairies. Year-round travellers can still hit the large retail shops \u2014 Cabot\u2019s Waterbury visitor center, Shelburne Farms Welcome Center, Grafton Village\u2019s Brattleboro store, and Pineland Farms Market \u2014 which stay open through the winter.',
  },
  {
    question: 'How far apart are the cheesery stops?',
    answer:
      'Burlington to Brattleboro is about 170 miles end-to-end; Portland to Camden about 80. A typical long-weekend route hits four or five cheeseries plus one or two stays. We recommend budgeting at least ninety minutes per stop once you include tasting, chatting with the cheesemaker, and the drive.',
  },
  {
    question: 'Is The Curd Compass really free, or is there a hidden cost?',
    answer:
      'The guide itself is free and nothing here is paid placement \u2014 inclusion is editorial. When you book a stay through a \u201cFind best rates\u201d button we may earn an affiliate commission from Booking.com, at no additional cost to you and only on completed stays. See the Affiliate Disclosure for the full picture.',
  },
  {
    question: 'What about cheesemakers outside Vermont, Maine, and New Hampshire?',
    answer:
      'The Curd Compass focuses on Northern New England — Vermont, Maine, and New Hampshire today, with Massachusetts and upstate New York on the roadmap. For Pennsylvania, we recommend our sister guide, the Pennsylvania Cheese Trail at pacheesetrail.com, which covers Amish-country creameries and Lancaster/Berks County dairies at the same editorial level.',
    answerRich: createElement(
      'span',
      null,
      'The Curd Compass focuses on Northern New England — Vermont, Maine, and New Hampshire today, with Massachusetts and upstate New York on the roadmap. For Pennsylvania, we recommend our sister guide, the ',
      createElement(
        'a',
        {
          href: 'https://pacheesetrail.com/',
          rel: 'noopener',
          className: 'text-accent font-semibold underline decoration-accent/40 underline-offset-2 hover:decoration-accent transition-colors',
        },
        'Pennsylvania Cheese Trail',
      ),
      ', which covers Amish-country creameries and Lancaster/Berks County dairies at the same editorial level.',
    ),
  },
  {
    question: 'What should I pair with Vermont cheddar?',
    answer:
      'The classic triangle is Vermont cheddar, a slice of apple (Cortland or Honeycrisp), and a pour of New England hard cider. For sharper two-year wheels like Cabot Clothbound or Shelburne Farms\u2019 aged cheddar, add stone-ground crackers and a dark ale.',
  },
  {
    question: 'Can I save my itinerary or use the map offline?',
    answer:
      'The itinerary you build on the site can be exported as a GPX file and loaded into any GPS or mapping app — Google Maps, Apple Maps, Gaia, Waze, Garmin, CalTopo — for offline navigation in the rural corners of the trail where cellular coverage is thin.',
  },
];
